import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Users, Eye, Trash2 } from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'

import { FieldLabel } from '../../components/ui/Field'

import { useAuthStore } from '../../stores/authStore'

import { toast } from 'sonner'
import {
  formatDate,
  formatDateTime,
  formatTime,
  dayKey,
  formatNumber,
} from '../../lib/format'
import {
  departmentList,
  reportKeyMetrics,
  workReport,
} from '../../lib/types'
import { KeyMetricsPanel } from '../../components/KeyMetricsPanel'

function fmtPeriod(r) {
  // Project-wide format: DD/MM/YYYY for single-day, "from → to" for ranges.
  if (r.type === 'daily') {
    return formatDate(r.periodStart)
  }
  return `${formatDate(r.periodStart)} → ${formatDate(r.periodEnd)}`
}

/**
 * Admin correction dialog — remove a Work Report filed under the wrong date.
 *
 * Deliberately NOT the shared ConfirmDialog: the reason is mandatory, so the
 * dialog needs a field, and the admin must see exactly WHOSE report and WHICH
 * period they are erasing before confirming. Deleting a compliance record off a
 * one-line "Are you sure?" would be too easy to get wrong.
 *
 * Users never see this — the menu item is admin-only, and the server re-checks.
 */
function DeleteReportDialog({ report, onCancel, onDeleted }) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const trimmed = reason.trim()

  async function confirm() {
    if (!trimmed) {
      setError('A reason is required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      // DELETE carries the reason in the body; the server treats a blank or
      // missing reason as a validation failure, so this can't be skipped.
      await api.delete(`/reports/${report.id}`, { data: { reason: trimmed } })
      onDeleted(report)
    } catch (err) {
      const { message, fields } = extractApiError(err)
      setError(fields?.reason ?? message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={Boolean(report)}
      onClose={submitting ? () => {} : onCancel}
      title="Delete this Work Report?"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            loading={submitting}
            disabled={!trimmed}
          >
            Delete Report
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Show exactly what is about to be erased, before the reason field. */}
        <dl className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] divide-y divide-[color:var(--color-rule)] text-sm">
          {[
            ['Employee', report?.userFullName ?? '—'],
            ['Report Type', (report?.type ?? '').toUpperCase() || '—'],
            ['Period', report ? fmtPeriod(report) : '—'],
            [
              'Current status',
              report?.submittedAt
                ? `Submitted ${formatDateTime(report.submittedAt)}`
                : 'Draft — not submitted',
            ],
          ].map(([label, value]) => (
            <div key={label} className="flex gap-4 px-3 py-2">
              <dt className="w-32 shrink-0 text-[color:var(--color-ink-muted)]">
                {label}
              </dt>
              <dd className="min-w-0 flex-1 text-[color:var(--color-ink-strong)] break-words">
                {value}
              </dd>
            </div>
          ))}
        </dl>

        <div>
          <FieldLabel label="Reason" htmlFor="delete-report-reason" required />
          <textarea
            id="delete-report-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value)
              if (error) setError(null)
            }}
            placeholder="e.g. Report submitted under incorrect date."
            aria-invalid={Boolean(error) || undefined}
            className={
              'w-full rounded-md border bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)] outline-none ' +
              (error
                ? 'border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)]')
            }
          />
          {error ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {error}
            </p>
          ) : null}
        </div>

        <p className="text-xs text-[color:var(--color-ink-muted)]">
          This permanently removes the report and is recorded in the Audit Log
          with your name and reason. The period becomes unfiled, so the employee
          can submit the correct report — their content is not moved for them.
        </p>
      </div>
    </Modal>
  )
}


/** Modal subtitle: reporting period + submitted timestamp. Collapses the
 *  submitted date to a time-only suffix when it lands on the same calendar
 *  day as the period (daily reports) so the date isn't printed twice;
 *  keeps the full date+time when they differ (weekly / monthly). */
function reportSubtitle(r) {
  const period = fmtPeriod(r)
  if (!r.submittedAt) return period
  const sameDay = dayKey(r.submittedAt) === dayKey(r.periodStart)
  const submitted = sameDay
    ? formatTime(r.submittedAt)
    : formatDateTime(r.submittedAt)
  return `${period} · Submitted ${submitted}`
}

/** Company Admin "Work Reports" — every submission across the tenant.
 *  Filterable by type, date range, and user (free-text on full name). */
export function WorkReportsPage() {
  // The detail view opens as a Modal — openReport holds the selected row so
  // the dialog renders its summary, work items, blockers, etc.
  const [openReport, setOpenReport] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const qc = useQueryClient()
  // Correcting a wrong-date report is an admin action. `canAdmin` on the
  // server grants the whole catalogue to any Sub Admin, so the client mirrors
  // that with a plain role check — the server re-checks regardless, so this
  // only hides an affordance, it does not enforce anything.
  const role = useAuthStore((st) => st.user?.role)
  const canDeleteReports =
    role === 'company_admin' || role === 'sub_admin' || role === 'super_admin'

  // "Period at a glance" KPI cards for the opened WEEKLY report — the same
  // panel the author sees. Fetched lazily only when a weekly report is opened,
  // under the same tenant + department/rank authorization as this list.
  const weeklyMetricsQuery = useQuery({
    queryKey: ['reports', 'metrics', openReport?.id],
    // Weekly AND monthly both carry a period rollup (backend supports both).
    enabled: Boolean(
      openReport &&
        (openReport.type === 'weekly' || openReport.type === 'monthly') &&
        openReport.id,
    ),
    queryFn: async () => {
      const res = await api.get(`/reports/${openReport.id}/metrics`)
      return {
        keyMetrics: reportKeyMetrics.parse(res.data?.keyMetrics ?? {}),
        meetings: Array.isArray(res.data?.meetings) ? res.data.meetings : null,
      }
    },
  })

  // Fetch the whole tenant-wide report set once; the DataTable does the
  // type / department / period / search filtering, sorting, pagination and
  // export entirely client-side. Each report is parsed INDEPENDENTLY so one
  // malformed legacy row can't blow up the list with a thrown ZodError.
  const reportsQuery = useQuery({
    queryKey: ['admin-reports'],
    queryFn: async () => {
      const res = await api.get('/reports')
      const raw = Array.isArray(res.data?.data) ? res.data.data : []
      const data = []
      for (const row of raw) {
        const parsed = workReport.safeParse(row)
        if (parsed.success) data.push(parsed.data)
        else
          console.warn(
            '[work reports] skipping malformed report',
            row?.id,
            parsed.error?.issues,
          )
      }
      return { data }
    },
  })

  // Departments for the filter dropdown. The list endpoint is nested under a
  // workspace, so fetch the admin's workspace(s) first then each one's
  // departments; single-workspace tenants resolve to one fetch.
  const departmentsQuery = useQuery({
    queryKey: ['work-reports-departments'],
    queryFn: async () => {
      const wsRes = await api.get('/workspaces/mine')
      const workspaces = wsRes.data?.data ?? []
      const lists = await Promise.all(
        workspaces.map(async (w) => {
          const res = await api.get(`/workspaces/${w.id}/departments`)
          return departmentList.parse(res.data).data
        }),
      )
      const byId = new Map()
      for (const d of lists.flat()) byId.set(String(d.id), d)
      return [...byId.values()]
    },
  })
  const departments = useMemo(
    () => departmentsQuery.data ?? [],
    [departmentsQuery.data],
  )

  const rows = reportsQuery.data?.data ?? []

  const columns = useMemo(
    () => [
      {
        key: 'type',
        header: 'Type',
        minWidth: 90,
        sortAccessor: (r) => (r.type ?? '').toLowerCase(),
        exportValue: (r) => (r.type ?? '').toUpperCase(),
        render: (r) => (
          <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
            {r.type}
          </span>
        ),
      },
      {
        key: 'fullName',
        header: 'Full Name',
        grow: true,
        pinnable: true,
        minWidth: 180,
        sortAccessor: (r) => (r.userFullName ?? '').toLowerCase(),
        searchAccessor: (r) => `${r.userFullName ?? ''} ${r.userEmailId ?? ''}`,
        exportValue: (r) => r.userFullName ?? '',
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink-strong)] truncate block">
            {r.userFullName}
          </span>
        ),
      },
      {
        key: 'email',
        header: 'Email ID',
        minWidth: 220,
        sortAccessor: (r) => (r.userEmailId ?? '').toLowerCase(),
        exportValue: (r) => r.userEmailId ?? '',
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink-muted)] truncate block">
            {r.userEmailId ?? '—'}
          </span>
        ),
      },
      {
        key: 'department',
        header: 'Department',
        minWidth: 160,
        sortAccessor: (r) => (r.departmentName ?? '').toLowerCase(),
        exportValue: (r) => r.departmentName ?? '',
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink-muted)] truncate block">
            {r.departmentName ?? '—'}
          </span>
        ),
      },
      {
        key: 'period',
        header: 'Period',
        minWidth: 170,
        sortAccessor: (r) => (r.periodStart ? new Date(r.periodStart) : null),
        exportValue: (r) => fmtPeriod(r),
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink)] truncate block">
            {fmtPeriod(r)}
          </span>
        ),
      },
      {
        key: 'submittedAt',
        header: 'Submitted',
        minWidth: 170,
        sortAccessor: (r) => (r.submittedAt ? new Date(r.submittedAt) : null),
        exportValue: (r) => (r.submittedAt ? formatDateTime(r.submittedAt) : ''),
        render: (r) => (
          <span className="text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap">
            {r.submittedAt ? formatDateTime(r.submittedAt) : '—'}
          </span>
        ),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action. Restore the
        // plural if a second is ever added — the eleven columns that already
        // carry two or more use "Actions".
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Actions for ${r.userFullName}`}
            items={[
              {
                key: 'view',
                label: 'View report',
                icon: Eye,
                onClick: () => setOpenReport(r),
              },
              // Admin-only. Removes a report filed under the wrong date so
              // the employee can submit the correct one; never offered to
              // the report’s own author.
              ...(canDeleteReports
                ? [
                    {
                      key: 'delete',
                      label: 'Delete report',
                      icon: Trash2,
                      danger: true,
                      onClick: () => setDeleteTarget(r),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    // `canDeleteReports` gates the Delete item, so the columns must rebuild
    // when the role resolves.
    [canDeleteReports],
  )

  const filterConfig = useMemo(
    () => [
      {
        key: 'type',
        label: 'Type',
        type: 'radio',
        anyLabel: 'All types',
        filterAccessor: (r) => r.type,
        options: [
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'monthly', label: 'Monthly' },
        ],
      },
      {
        key: 'department',
        label: 'Department',
        type: 'select',
        anyLabel: 'All departments',
        filterAccessor: (r) => String(r.departmentId ?? ''),
        options: departments.map((d) => ({
          value: String(d.id),
          label: d.name,
        })),
      },
      {
        key: 'period',
        label: 'Reporting period',
        type: 'daterange',
        filterAccessor: (r) => (r.periodStart ?? '').slice(0, 10),
        hint: 'Filter by the period a report covers.',
      },
    ],
    [departments],
  )

  return (
    <div>
      <Breadcrumb current="Work Reports" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <FileText size={20} className="text-[color:var(--color-accent-strong)]" />
          Work Reports
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Review your team's reports.
        </p>
      </div>

      <DataTable
        tableId="app-work-reports"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={reportsQuery.isLoading}
        error={
          reportsQuery.isError
            ? extractApiError(reportsQuery.error).message
            : null
        }
        onRetry={() => reportsQuery.refetch()}
        title="Work Reports"
        exportBaseName="work-reports"
        exportTitle="Work Reports"
        selectable
        searchPlaceholder="Search by Full Name or Email ID"
        filterConfig={filterConfig}
        defaultSort={[{ key: 'submittedAt', dir: 'desc' }]}
        emptyState={{
          icon: Users,
          title: 'No reports submitted yet',
          body: 'Reports will appear here once your team starts submitting them.',
        }}
      />

      {/* Detail dialog — opens when a row is clicked. Wider `xl` size
       *  so the embedded Work Details table doesn't get cramped, and
       *  the Modal's own max-h-[90vh] + body scroll handles the tall
       *  content (summary → work items → blockers → plans → meetings
       *  → notes). */}
      {/* Admin correction — remove a report filed under the wrong date. */}
      {deleteTarget ? (
        <DeleteReportDialog
          report={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onDeleted={(r) => {
            setDeleteTarget(null)
            // If the deleted report is open behind the dialog, close it too.
            if (openReport?.id === r.id) setOpenReport(null)
            void qc.invalidateQueries({ queryKey: ['admin-reports'] })
            toast.success(
              `Work report deleted. ${r.userFullName ?? 'The employee'} can now submit the correct report for this period.`,
            )
          }}
        />
      ) : null}
      <Modal
        open={Boolean(openReport)}
        onClose={() => setOpenReport(null)}
        size="xl"
        // Wider than the default `xl` (max-w-4xl): this dialog embeds the
        // full 7-column Work Details table, so give the columns — especially
        // the wrapping Task/description cell — more room to breathe.
        widthClass="max-w-6xl"
        title={
          openReport
            ? `${openReport.type.charAt(0).toUpperCase()}${openReport.type.slice(1)} report · ${openReport.userFullName}`
            : ''
        }
        /* Reporting period + when it was actually filed — two different
         *  fields. `fmtPeriod` is the day(s) the report COVERS (a date
         *  range; its `periodStart` is a midnight boundary with no
         *  meaningful time), `submittedAt` is the timestamp the table's
         *  "Submitted" column shows.
         *
         *  On a DAILY report both fall on the same calendar day, so
         *  printing the full submitted date after the period repeated the
         *  date ("17/07/2026 · Submitted 17/07/2026 05:44 PM"). When the
         *  submitted day matches the period day we show the TIME only
         *  ("Submitted 05:44 PM"); when they differ (weekly / monthly,
         *  filed after the window closed) we keep the full timestamp so
         *  the date is still there. Same formatters as the table. */
        description={
          openReport ? reportSubtitle(openReport) : undefined
        }
        footer={
          <Button variant="secondary" onClick={() => setOpenReport(null)}>
            Close
          </Button>
        }
      >
        {openReport ? (
          <div className="space-y-5">
            {/* Period at a glance — weekly reports only. Same KPI panel the
             *  author sees; metrics fetched under the same authorization as
             *  this Team Reports list. */}
            {(openReport.type === 'weekly' || openReport.type === 'monthly') &&
            weeklyMetricsQuery.data ? (
              <KeyMetricsPanel
                metrics={weeklyMetricsQuery.data.keyMetrics}
                periodType={openReport.type}
                caption="Computed from daily reports"
              />
            ) : null}
            {openReport.content.summary ? (
              <div>
                <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
                  Summary
                </h3>
                <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap">
                  {openReport.content.summary}
                </p>
              </div>
            ) : null}
            <WorkItemsBlock items={openReport.content.workItems} />
            {/* Accomplishments — captured by the Weekly + Monthly
             *  report forms (see `ReportsPage.jsx` line ~741). The
             *  viewer used to hide this entirely on the assumption
             *  it had also been dropped from the daily form, but
             *  weekly/monthly never lost it — users fill it in and
             *  expect to see it on read-back. ListBlock renders
             *  nothing when the array is empty, so daily reports
             *  (which don't have this section) stay clean. */}
            <ListBlock
              label="Accomplishments"
              items={openReport.content.accomplishments}
            />
            <BlockersBlock items={openReport.content.blockers} />
            <ListBlock
              // Renamed from "Plan for Tomorrow" to "Plans for Next"
              // so the label matches the form field. The daily
              // report's tomorrow-specific framing was confusing on
              // weekly/monthly reports (where "next" means next
              // week / next month, not "tomorrow").
              label="Plans for Next"
              items={openReport.content.plansForNext}
            />
            {/* Weekly/monthly: rolled-up meetings from the period's dailies
             *  (reflects a daily meeting added after filing); daily shows its
             *  own. Falls back to the filed snapshot until the rollup loads. */}
            <MeetingsBlock
              items={
                (openReport.type === 'weekly' || openReport.type === 'monthly') &&
                weeklyMetricsQuery.data?.meetings
                  ? weeklyMetricsQuery.data.meetings
                  : openReport.content.meetings
              }
            />
            {openReport.content.notes ? (
              <div>
                <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
                  Notes
                </h3>
                <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap">
                  {openReport.content.notes}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

function ListBlock({ label, items }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
        {label}
      </h3>
      <ul className="list-disc pl-5 space-y-0.5 text-sm text-[color:var(--color-ink)]">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  )
}

/** Read-only render of the Work-details table the user filled in. Shows
 *  every column so the admin sees the same level of detail the user
 *  typed: task, project, module, type, status, priority, hours. */
function WorkItemsBlock({ items }) {
  if (!items || items.length === 0) return null
  const totalHours = items.reduce(
    (sum, it) => sum + Number(it.timeSpent ?? 0),
    0,
  )
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
          Work Details
        </h3>
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          {items.length} {items.length === 1 ? 'task' : 'tasks'} ·{' '}
          {formatNumber(totalHours, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h
        </p>
      </div>
      <div className="overflow-x-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-left bg-[color:var(--color-canvas)]">
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Task
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Project
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Module
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Type
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Status
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Priority
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] text-right">
                Time (hours)
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr
                key={i}
                className="align-top border-t border-[color:var(--color-rule)]"
              >
                <td className="px-4 py-3 text-[color:var(--color-ink-strong)]">
                  <p className="font-medium">{it.title ?? '—'}</p>
                  {it.description ? (
                    <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] whitespace-pre-wrap text-justify">
                      {it.description}
                    </p>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {it.project || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {it.module || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {(it.type ?? '').replace(/_/g, ' ') || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {(it.status ?? '').replace(/_/g, ' ') || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {it.priority || '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-[color:var(--color-ink-strong)]">
                  {formatNumber(Number(it.timeSpent ?? 0), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Read-only render of the Blockers section. Same columns as the user's
 *  form: blocker text, who/what it depends on, and resolution status. */
function BlockersBlock({ items }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
        Issues & Dependencies
      </h3>
      <div className="overflow-x-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-left bg-[color:var(--color-canvas)]">
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Blocker
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Dependency
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((b, i) => (
              <tr
                key={i}
                className="align-top border-t border-[color:var(--color-rule)]"
              >
                <td className="px-4 py-3 text-[color:var(--color-ink-strong)]">
                  {b.blocker || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {b.dependencyOn || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {(b.resolutionStatus ?? '').replace(/_/g, ' ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Read-only render of the Meetings section. Meeting (title, with any
 *  legacy purpose as subtext) / duration in minutes / attendees. */
function MeetingsBlock({ items }) {
  if (!items || items.length === 0) return null
  // Date column only when at least one meeting carries one — rollup
  // meetings are stamped with the daily they came from (see
  // TeamMeetingsBlock in employee/ReportsPage.jsx, same treatment).
  const hasDates = items.some((m) => m.date)
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
        Communication & Collaboration
      </h3>
      <div className="overflow-x-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-left bg-[color:var(--color-canvas)]">
              {hasDates ? (
                <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] whitespace-nowrap">
                  Date
                </th>
              ) : null}
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Meeting
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] text-right">
                Duration (min)
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Attendees
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((m, i) => (
              <tr
                key={i}
                className="align-top border-t border-[color:var(--color-rule)]"
              >
                {hasDates ? (
                  <td className="px-4 py-3 text-[color:var(--color-ink)] tabular-nums whitespace-nowrap">
                    {m.date ? formatDate(m.date) : '—'}
                  </td>
                ) : null}
                <td className="px-4 py-3 text-[color:var(--color-ink-strong)] font-medium">
                  {m.title || '—'}
                  {/* Legacy reports stored purpose separately — show it as
                   *  subtext so merging the column doesn't hide old data. */}
                  {m.purpose ? (
                    <span className="block font-normal text-[color:var(--color-ink-muted)]">
                      {m.purpose}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-[color:var(--color-ink-strong)]">
                  {m.durationMinutes ?? 0}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {m.attendees || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
